import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createTrend, updateTrend, fetchTrendById } from './trendSlice';
import { fetchCategories } from '../category/categorySlice';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import log from '../../logger';
import ReactForm from '../../components/ReactForm';
import ReactDropdown from '../../components/ReactDropdown';
import { paths } from '../../routes/helpers';
import { getImageUrl, toLocalUploadPath } from '../../utils/urlUtils';
import ImageGallery from '../../components/ImageGallery';

const TrendCreateEdit = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { trendId } = useParams();
  const loading = useSelector((state) => state.trend.loading);
  const error = useSelector((state) => state.trend.error);
  const trend = useSelector((state) => 
    trendId ? state.trend.items.find(b => b._id === trendId) : null
  );
  const categories = useSelector((state) => state.category.items);
  const categoriesLoading = useSelector((state) => state.category.loading);

  const [imageFiles, setImageFiles] = useState([]);
  const [existingImage, setExistingImage] = useState('');
  const [selectedCategories, setSelectedCategories] = useState([]);

  useEffect(() => {
    // Fetch categories when component mounts
    dispatch(fetchCategories({ limit: 100 }));
    
    if (trendId) {
      dispatch(fetchTrendById(trendId));
    }
  }, [trendId, dispatch]);

  useEffect(() => {
    if (trend) {
      if (trend.image) {
        setExistingImage(getImageUrl(trend.image));
      }
      if (trend.categoryIds) {
        // Handle both array and single category scenarios for backward compatibility
        const categoryIds = Array.isArray(trend.categoryIds) ? trend.categoryIds : [trend.categoryIds];
        const selectedCats = categoryIds.map(cat => ({
          value: cat._id || cat,
          label: cat.name || cat.label || cat
        }));
        setSelectedCategories(selectedCats);
      }
    }
  }, [trend]);

  const handleImageChange = (event) => {
    const files = Array.from(event.target.files);
    setImageFiles(files);
  };

  const handleRemoveImage = (index) => {
    setImageFiles(prevFiles => prevFiles.filter((_, i) => i !== index));
  };

  const handleRemoveExistingImage = () => {
    setExistingImage('');
  };

  const handleCategoriesChange = (selected) => {
    setSelectedCategories(selected || []);
  };

  const handleSubmit = async (formData) => {
    try {
      const processedData = new FormData();
      processedData.append('label', formData.label);
      processedData.append('status', parseInt(formData.status));
      
      // Handle multiple categories
      const categoryIds = selectedCategories.map(cat => cat.value);
      categoryIds.forEach(id => {
        processedData.append('categoryIds', id);
      });
      
      if (imageFiles.length > 0) {
        processedData.append('image', imageFiles[0]);
      } else if (existingImage && trendId) {
        // Keep existing image
        processedData.append('existingImage', toLocalUploadPath(existingImage));
      }

      if (trendId) {
        await dispatch(updateTrend({ id: trendId, formData: processedData })).unwrap();
        toast.success('Trend updated successfully!');
        log.info('Trend updated:', formData.label);
      } else {
        await dispatch(createTrend(processedData)).unwrap();
        toast.success('Trend created successfully!');
        log.info('Trend created:', formData.label);
      }
      navigate(paths.private.trend.list());
    } catch (err) {
      toast.error(err.message || 'An error occurred');
      log.error('Error creating/updating trend:', err);
    }
  };

  const categoryOptions = categories.map(category => ({
    value: category._id,
    label: category.name || category.label
  }));

  const fields = [
    {
      name: 'label',
      label: 'Label',
      type: 'text',
      required: true,
      placeholder: 'Enter trend label'
    },
    {
      name: 'categoryIds',
      label: 'Categories',
      type: 'dropdown',
      required: true,
      component: (
        <ReactDropdown
          options={categoryOptions}
          value={selectedCategories}
          onChange={handleCategoriesChange}
          isMulti={true}
          required={true}
          placeholder="Select categories"
          isLoading={categoriesLoading}
        />
      )
    },
    {
      name: 'image',
      label: 'Image',
      type: 'file',
      accept: 'image/*',
      required: !trendId || (!existingImage && imageFiles.length === 0),
      onChange: handleImageChange,
      preview: (
        <ImageGallery 
          existingImages={imageFiles.length > 0 ? [] : existingImage ? [existingImage] : []}
          imageFiles={imageFiles}
          onRemoveExisting={handleRemoveExistingImage}
          onRemoveNew={handleRemoveImage}
          loading={loading && trend?.image && !existingImage}
        />
      )
    },
    {
      name: 'status',
      label: 'Status',
      type: 'select',
      required: true,
      defaultValue: "0",
      options: [
        { value: "0", label: 'Inactive' },
        { value: "1", label: 'Active' },
        { value: "2", label: 'Suspended' }
      ]
    }
  ];

  const defaultValues = trend ? {
    label: trend.label,
    image: [],
    status: trend.status.toString()
  } : {
    label: '',
    image: [],
    status: "1"
  };

  log.info('Default values:', defaultValues);

  return (
    <div className="p-4">
      <h2 className="text-xl font-bold mb-4">
        {trendId ? 'Edit Trend' : 'Create Trend'}
      </h2>
      <ReactForm
        fields={fields}
        onSubmit={handleSubmit}
        loading={loading || categoriesLoading}
        error={error}
        defaultValues={defaultValues}
        submitText={trendId ? 'Update Trend' : 'Create Trend'}
      />
    </div>
  );
};

export default TrendCreateEdit;